import * as React from "react";
import { Button, Grow } from "@mui/material";
import { format } from "@redtea/format-axios-error";
import { isAxiosError } from "axios";
import { closeSnackbar, enqueueSnackbar } from "notistack";
import { isAccessDeniedPresenting } from "@app/common/access/accessDeniedBus";
import { DENIAL_SNACKBAR_MESSAGE, isForbiddenError } from "@app/common/access/denialCopy";
// Shared entrance/exit treatment for every <SnackbarProvider> in the app (brokerage,
// login, FUB embedded app) — matches native MUI <Snackbar>'s own default transition
// exactly (it uses Grow, not notistack's default flat Slide): same component, and the
// same theme.transitions.duration.{enteringScreen,leavingScreen} timing (220/180 here).
export const SNACKBAR_TRANSITION_COMPONENT = Grow;
export const SNACKBAR_TRANSITION_DURATION = { enter: 220, exit: 180 };
const ActionButton = (props) => {
    const { error, snackbarKey } = props;
    const isAxiosError_ = isAxiosError(error);
    const dismiss = React.useCallback(() => {
        closeSnackbar(snackbarKey);
    }, [snackbarKey]);
    const onClick = React.useCallback(() => {
        const newWindow = window.open("", "_blank");
        if (newWindow) {
            const doc = newWindow.document;
            const errorStr = isAxiosError_ ? JSON.stringify(format(error), null, 2) : String(error);
            const pre = doc.createElement("pre");
            pre.textContent = errorStr;
            doc.body.appendChild(pre);
            doc.close();
        }
        dismiss();
    }, [isAxiosError_, error, dismiss]);
    return (React.createElement(React.Fragment, null,
        React.createElement(Button, { onClick: dismiss, style: { color: "white" } }, "Dismiss"),
        isAxiosError_ && (React.createElement(Button, { onClick: onClick, style: { color: "white" } }, "See more"))));
};
// Title-case a serializer field key for display: `to_phone` -> "To Phone", `reviewFrequency` -> "Review Frequency".
const humanizeFieldName = (key) => key
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/[_-]+/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .replace(/\b\w/g, (c) => c.toUpperCase());
// DRF "blank/required/null" messages — these get the friendlier "Missing Required Field" phrasing.
const REQUIRED_FIELD_PATTERNS = ["may not be blank", "is required", "may not be null", "no file was submitted"];
// Walk a DRF validation error body ({field: [msg]}, possibly nested under a wrapper like
// `sms_template`) to the first leaf message, tracking the DEEPEST field key so the label is the
// actual field ("to_phone") rather than the wrapper ("sms_template").
const findFirstFieldError = (data, fieldKey) => {
    if (typeof data === "string") {
        return { field: fieldKey, message: data };
    }
    if (Array.isArray(data)) {
        for (const item of data) {
            const found = findFirstFieldError(item, fieldKey);
            if (found) {
                return found;
            }
        }
        return null;
    }
    if (typeof data === "object" && data !== null) {
        for (const [key, value] of Object.entries(data)) {
            const found = findFirstFieldError(value, key);
            if (found) {
                return found;
            }
        }
    }
    return null;
};
// Turn a DRF field-validation 400 body into a legible toast, or null if it isn't one. E.g.
// `{sms_template: {to_phone: ["This field may not be blank."]}}` -> "Missing Required Field: 'To Phone'".
const formatValidationError = (data) => {
    if (typeof data !== "object" || data === null || Array.isArray(data)) {
        return null;
    }
    const found = findFirstFieldError(data, null);
    if (!found) {
        return null;
    }
    const { field, message } = found;
    if (field === null || field === "non_field_errors" || field === "detail") {
        return message;
    }
    const label = humanizeFieldName(field);
    if (REQUIRED_FIELD_PATTERNS.some((pattern) => message.toLowerCase().includes(pattern))) {
        return `Missing Required Field: '${label}'`;
    }
    return `${label}: ${message}`;
};
export const enqueueErrorSnackbar = (error, params) => {
    var _a, _b;
    const action = (key) => {
        return React.createElement(ActionButton, { snackbarKey: key, error: error });
    };
    // A 403 is a denial, not a generic error. The global interceptor already
    // routes it to the designed access-denied dialog; if that dialog is presenting
    // this same denial, don't ALSO toast it (avoid double-presentation). Otherwise
    // -- an opted-out call site, or no dialog host -- show the friendly denial copy
    // (headline + tech@ line) instead of the raw server detail/JSON.
    if (isForbiddenError(error)) {
        const url = isAxiosError(error) ? (_a = error.config) === null || _a === void 0 ? void 0 : _a.url : undefined;
        if (isAccessDeniedPresenting(url)) {
            return;
        }
        enqueueSnackbar((_b = params === null || params === void 0 ? void 0 : params.label) !== null && _b !== void 0 ? _b : DENIAL_SNACKBAR_MESSAGE, Object.assign({ variant: "error", persist: true, action }, params === null || params === void 0 ? void 0 : params.options));
        return;
    }
    const label = (() => {
        var _a, _b, _c, _d, _e, _f, _g, _h;
        if (typeof error === "string") {
            // Guard against callers passing a raw JSON payload as the error string
            // (e.g. a background-task response body leaking into the error path). These
            // aren't human-readable, so fall back to a generic message. Raw content is
            // still available in the console for debugging.
            const trimmed = error.trim();
            if (trimmed.startsWith("{") || trimmed.startsWith("[")) {
                return (_a = params === null || params === void 0 ? void 0 : params.label) !== null && _a !== void 0 ? _a : "An unexpected error occurred. Please try again.";
            }
            return error;
        }
        if (params === null || params === void 0 ? void 0 : params.label) {
            return params.label;
        }
        if (Array.isArray((_b = error.response) === null || _b === void 0 ? void 0 : _b.data) && error.response.data.every((x) => typeof x === "string")) {
            return (_c = error.response) === null || _c === void 0 ? void 0 : _c.data.join("; ");
        }
        if (((_e = (_d = error.response) === null || _d === void 0 ? void 0 : _d.data) === null || _e === void 0 ? void 0 : _e.detail) && typeof error.response.data.detail === "string") {
            return error.response.data.detail;
        }
        if (((_g = (_f = error.response) === null || _f === void 0 ? void 0 : _f.data) === null || _g === void 0 ? void 0 : _g.error) && typeof error.response.data.error === "string") {
            return error.response.data.error;
        }
        // DRF field-validation errors arrive as a nested `{field: [msg]}` object (e.g. saving a
        // template with a blank required field). Surface the offending field legibly instead of a
        // generic message.
        const validationMessage = formatValidationError((_h = error.response) === null || _h === void 0 ? void 0 : _h.data);
        if (validationMessage) {
            return validationMessage;
        }
        return "An error occurred";
    })();
    enqueueSnackbar(label.length > 255 ? `${label.slice(0, 255)}...` : label, Object.assign({ variant: "error", persist: true, action }, params === null || params === void 0 ? void 0 : params.options));
};
